import { Alert } from "@shared-kernel/primary/shared/alert";
import { EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS } from "@academy-context/shared/domain/types/enums/external-provider-batch-request";

interface Props {
  currentStatus: EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS;
  isEvaluationSubmitted: boolean;
  isInvoiceSubmitted: boolean;
}

type StatusMessageInfo = {
  variant: "info" | "success" | "error" | "warning";
  message: string;
  action?: string;
};

const getStatusMessage = (
  status: EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS,
  isEvaluationSubmitted: boolean,
  isInvoiceSubmitted: boolean,
): StatusMessageInfo => {
  switch (status) {
    // Review phase - action required
    case EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.REQUEST_PENDING_PROVIDER_VERIFICATION:
      return {
        variant: "warning",
        message: "Demande en attente de vérification.",
        action: "Veuillez vérifier et compléter les informations ci-dessous, puis valider la demande.",
      };
    case EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.REQUEST_REFUSED:
      return {
        variant: "error",
        message: "Votre demande a été refusée.",
        action: "Veuillez corriger les informations et soumettre à nouveau.",
      };

    // Pending validation phase - no action
    case EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.REQUEST_TO_VALIDATE:
      return {
        variant: "success",
        message:
          "Nous vous remercions pour ces informations. Vous pouvez désormais quitter cette page. Vous recevrez le contrat de sous-traitance par courriel dans un délai de 24-48 heures.",
      };
    case EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.REQUEST_APPROVED:
      return {
        variant: "success",
        message: "Votre demande a été approuvée. La formation sera bientôt planifiée.",
      };
    case EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.SCHEDULED:
      return {
        variant: "info",
        message: "La formation est planifiée. Vous pourrez soumettre votre facture une fois la formation terminée.",
      };
    case EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.RUNNING:
      return {
        variant: "info",
        message: "La formation est en cours. Vous pourrez soumettre votre facture une fois la formation terminée.",
      };

    // Invoice phase - action may be required
    case EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.WAITING_FOR_INVOICE: {
      const hasPendingEvaluation = !isEvaluationSubmitted;
      const needsInvoice = !isInvoiceSubmitted;
      let action: string;

      if (hasPendingEvaluation && needsInvoice) {
        action = "Veuillez rendre votre évaluation et soumettre votre facture.";
      } else if (hasPendingEvaluation) {
        action = "Veuillez rendre votre évaluation : chaque objectif noté, puis votre appréciation.";
      } else if (needsInvoice) {
        action = "Veuillez soumettre votre facture.";
      } else {
        action = "Votre évaluation et votre facture sont complètes.";
      }

      return {
        variant: "warning",
        message: "La formation est terminée.",
        action,
      };
    }
    case EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.INVOICE_REFUSED:
      return {
        variant: "error",
        message: "Votre facture a été refusée.",
        action: "Veuillez corriger et soumettre à nouveau.",
      };
    case EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.INVOICE_TO_VALIDATE_BY_ADMIN:
      return {
        variant: "info",
        message: "Votre facture est en attente de validation par l'administrateur.",
      };
    case EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.INVOICE_APPROVED:
      return {
        variant: "success",
        message: "Votre facture a été approuvée. Le paiement sera effectué prochainement.",
      };
    case EXTERNAL_PROVIDER_BATCH_PROVIDER_MERGED_STATUS.PAID:
      return {
        variant: "success",
        message: "Le paiement a été effectué. Merci pour votre collaboration !",
      };
  }
};

export const DashboardHeader = ({ currentStatus, isEvaluationSubmitted, isInvoiceSubmitted }: Props) => {
  const statusMessage = getStatusMessage(currentStatus, isEvaluationSubmitted, isInvoiceSubmitted);

  return (
    <Alert variant={statusMessage.variant} className="text-sm">
      <p>{statusMessage.message}</p>
      {statusMessage.action && <p className="mt-1 font-medium">{statusMessage.action}</p>}
    </Alert>
  );
};
